import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ProfessionalLoginCard } from './ProfessionalLoginCard';
import { ProfessionalLedgerDrawer } from './ProfessionalLedgerDrawer';
import { 
  Users, 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Phone, 
  Percent, 
  Calendar, 
  CheckCircle2, 
  XCircle, 
  Upload, 
  Loader2, 
  X, 
  UserCheck, 
  UserPlus,
  Briefcase, 
  ShieldCheck, 
  Filter,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  UserX,
  Layers,
  Smartphone,
  Copy,
  Eye,
  EyeOff,
  Key,
  MapPin,
  Wallet,
  CreditCard,
  Building2,
  FileText,
  User
} from 'lucide-react';

export interface ServiceCategory {
  id: string;
  category_name: string;
  parent_category_id?: string | null;
  is_active: boolean;
}

export interface ServiceProfessionalSkill {
  id: string;
  professional_id: string;
  category_id: string;
  service_categories?: {
    id: string;
    category_name: string;
  } | null;
}

export interface ServiceProfessional {
  id: string;
  tenant_id: string;
  branch_id: string;
  user_tenant_role_id?: string | null;
  supervisor_id?: string | null;
  service_provider_id?: string | null;
  full_name: string;
  phone: string;
  commission_percent: number;
  photo?: string | null;
  joined_at?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  service_professional_skills?: ServiceProfessionalSkill[];
  service_professional_kyc?: {
    city?: string | null;
    aadhaar_number?: string | null;
    address_line?: string | null;
    pan_number?: string | null;
  } | {
    city?: string | null;
    aadhaar_number?: string | null;
    address_line?: string | null;
    pan_number?: string | null;
  }[] | null;
  supervisor?: {
    id: string;
    full_name: string;
  } | null;
}

export interface UserTenantRoleItem {
  id: string;
  user_id: string;
  role_name: string;
  user_email?: string;
  full_name?: string;
}

interface StaffTeamViewProps {
  branchId?: string | null;
}

interface SupervisorGroup {
  supervisorId: string | null;
  supervisorName: string;
  supervisorPro?: ServiceProfessional | null;
  members: ServiceProfessional[];
}

export function StaffTeamView({ branchId }: StaffTeamViewProps = {}) {
  const { currentTenantId, activeBranchId, checkServiceProviderRole } = useAuth();
  const supabase = getSupabaseClient();

  // Data states
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [tenantRoles, setTenantRoles] = useState<UserTenantRoleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedBranchId, setResolvedBranchId] = useState<string | null>(branchId || activeBranchId || null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [viewGrouping, setViewGrouping] = useState<'all' | 'by_supervisor'>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // Add / Edit Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPro, setEditingPro] = useState<ServiceProfessional | null>(null);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [commissionPercent, setCommissionPercent] = useState<number | string>(10);
  const [joinedAt, setJoinedAt] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [selectedSkillCategoryIds, setSelectedSkillCategoryIds] = useState<string[]>([]);
  const [selectedRoleLinkId, setSelectedRoleLinkId] = useState<string>('');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<string>('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [proIsActive, setProIsActive] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);

  // Professional Profile & Identity (Address, KYC, Bank)
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [dob, setDob] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [showAadhaar, setShowAadhaar] = useState(false);
  const [panNumber, setPanNumber] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [idProofUrl, setIdProofUrl] = useState<string | null>(null);
  const [uploadingIdProof, setUploadingIdProof] = useState(false);
  const [bankHolder, setBankHolder] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [upiId, setUpiId] = useState('');

  // Collapsible sections & Retry state
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [isBankExpanded, setIsBankExpanded] = useState(false);
  const [pendingProfileProId, setPendingProfileProId] = useState<string | null>(null);
  const [profileSaveError, setProfileSaveError] = useState<string | null>(null);

  // Selected Professional for Ledger Drawer
  const [selectedLedgerPro, setSelectedLedgerPro] = useState<ServiceProfessional | null>(null);

  // Delete Confirmation Modal
  const [deleteTarget, setDeleteTarget] = useState<ServiceProfessional | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Dedicated App Login Management Modal
  const [loginManageTarget, setLoginManageTarget] = useState<ServiceProfessional | null>(null);

  // Self-as-Professional state (PATCH #2ad Task A)
  const [enablingSelf, setEnablingSelf] = useState(false);
  const [selfEnableError, setSelfEnableError] = useState<string | null>(null);

  const ownerProfessional = useMemo(() => {
    return professionals.find(p => p.full_name?.includes('(Owner)'));
  }, [professionals]);

  const handleEnableSelfAsProfessional = async () => {
    if (!supabase || !currentTenantId) return;
    setEnablingSelf(true);
    setSelfEnableError(null);
    try {
      const { data, error } = await supabase.rpc('enable_self_as_professional', {
        p_tenant_id: currentTenantId,
      });

      if (error) {
        const msg = error.message || error.details || 'Failed to enable self as professional';
        setSelfEnableError(msg);
        toast.error(msg);
        return;
      }

      await loadData();
      if (checkServiceProviderRole) {
        await checkServiceProviderRole();
      }

      toast.success('You are now listed as professional (Owner)');
    } catch (err: any) {
      const msg = err?.message || 'Error enabling self as professional';
      setSelfEnableError(msg);
      toast.error(msg);
    } finally {
      setEnablingSelf(false);
    }
  };

  // Resolve valid Branch ID
  useEffect(() => {
    if (branchId) {
      setResolvedBranchId(branchId);
    } else if (activeBranchId) {
      setResolvedBranchId(activeBranchId);
    } else if (supabase && currentTenantId) {
      supabase
        .from('branches')
        .select('id')
        .eq('tenant_id', currentTenantId)
        .limit(1)
        .maybeSingle()
        .then(({ data }: { data: any }) => {
          if (data?.id) setResolvedBranchId(data.id);
        });
    }
  }, [branchId, activeBranchId, supabase, currentTenantId]);

  // Load all master data
  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch Service Categories for skill mapping
      const { data: catData, error: catErr } = await supabase
        .from('service_categories')
        .select('id, category_name, parent_category_id, is_active')
        .eq('tenant_id', currentTenantId)
        .order('category_name', { ascending: true });
      if (catErr) throw catErr;
      setCategories(catData || []);

      // 2. Fetch Professionals with joined Skills and supervisor info
      const { data: proData, error: proErr } = await supabase
        .from('service_professionals')
        .select(`
          id,
          tenant_id,
          branch_id,
          user_tenant_role_id,
          supervisor_id,
          service_provider_id,
          full_name,
          phone,
          commission_percent,
          photo,
          joined_at,
          is_active,
          created_at,
          updated_at,
          service_professional_skills (
            id,
            professional_id,
            category_id,
            service_categories (
              id,
              category_name
            )
          ),
          service_professional_kyc (
            city,
            aadhaar_number,
            address_line,
            pan_number
          )
        `)
        .eq('tenant_id', currentTenantId)
        .order('created_at', { ascending: false });
      if (proErr) throw proErr;
      setProfessionals((proData as any[]) || []);

      // 3. Fetch Tenant User Roles for optional linking
      const { data: rolesData } = await supabase
        .from('user_tenant_roles')
        .select('id, user_id, role_name')
        .eq('tenant_id', currentTenantId);
      setTenantRoles((rolesData as any[]) || []);
    } catch (err: any) {
      console.error('Error loading service professionals data:', err);
      toast.error('Failed to load staff list: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fast supervisor name lookup map
  const supervisorNameMap = useMemo(() => {
    const map = new Map<string, string>();
    professionals.forEach(p => {
      map.set(p.id, p.full_name);
    });
    return map;
  }, [professionals]);

  // Count of supervisees reporting to each professional
  const superviseeCountMap = useMemo(() => {
    const counts: Record<string, number> = {};
    professionals.forEach(p => {
      if (p.supervisor_id) {
        counts[p.supervisor_id] = (counts[p.supervisor_id] || 0) + 1;
      }
    });
    return counts;
  }, [professionals]);

  // Available supervisors for the dropdown in modal:
  // Must be active, same tenant, and exclude the professional currently being edited
  const availableSupervisors = useMemo(() => {
    return professionals.filter(
      p => p.is_active && (!editingPro || p.id !== editingPro.id)
    );
  }, [professionals, editingPro]);

  // Filtered Professionals
  const filteredProfessionals = useMemo(() => {
    return professionals.filter(pro => {
      // Status filter
      if (statusFilter === 'active' && !pro.is_active) return false;
      if (statusFilter === 'inactive' && pro.is_active) return false;

      // Category filter
      if (selectedCategoryFilter !== 'all') {
        const hasSkill = pro.service_professional_skills?.some(
          s => s.category_id === selectedCategoryFilter
        );
        if (!hasSkill) return false;
      }

      // Search query filter (name or phone or skill name or supervisor name)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = pro.full_name?.toLowerCase().includes(q);
        const matchesPhone = pro.phone?.includes(q);
        const matchesSkill = pro.service_professional_skills?.some(s =>
          s.service_categories?.category_name?.toLowerCase().includes(q)
        );
        const supervisorName = pro.supervisor_id ? supervisorNameMap.get(pro.supervisor_id)?.toLowerCase() : '';
        const matchesSupervisor = supervisorName?.includes(q);
        if (!matchesName && !matchesPhone && !matchesSkill && !matchesSupervisor) return false;
      }

      return true;
    });
  }, [professionals, statusFilter, selectedCategoryFilter, searchQuery, supervisorNameMap]);

  // Grouped by supervisor client-side
  const groupedBySupervisor = useMemo(() => {
    const groups: Record<string, ServiceProfessional[]> = {};
    const unassigned: ServiceProfessional[] = [];

    filteredProfessionals.forEach(pro => {
      if (pro.supervisor_id) {
        if (!groups[pro.supervisor_id]) {
          groups[pro.supervisor_id] = [];
        }
        groups[pro.supervisor_id].push(pro);
      } else {
        unassigned.push(pro);
      }
    });

    const result: SupervisorGroup[] = [];

    // Add groups with assigned supervisors
    Object.keys(groups).forEach(supId => {
      const supPro = professionals.find(p => p.id === supId);
      result.push({
        supervisorId: supId,
        supervisorName: supPro ? supPro.full_name : 'Unknown Supervisor',
        supervisorPro: supPro || null,
        members: groups[supId],
      });
    });

    // Sort supervisor groups alphabetically
    result.sort((a, b) => a.supervisorName.localeCompare(b.supervisorName));

    // Append unassigned group at the end
    if (unassigned.length > 0) {
      result.push({
        supervisorId: null,
        supervisorName: 'Unassigned / Direct Staff',
        supervisorPro: null,
        members: unassigned,
      });
    }

    return result;
  }, [filteredProfessionals, professionals]);

  const toggleGroupCollapse = (key: string) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Aadhaar Formatter Helper
  const formatAadhaarInput = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 12);
    const parts = [];
    for (let i = 0; i < digits.length; i += 4) {
      parts.push(digits.slice(i, i + 4));
    }
    return parts.join(' ');
  };

  // Fetch Full Profile via get_professional_profile RPC
  const fetchProProfile = async (proId: string) => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase.rpc('get_professional_profile', {
        p_professional_id: proId,
      });
      if (error) {
        console.warn('get_professional_profile error:', error);
      } else if (data) {
        setAddressLine(data.address_line || '');
        setCity(data.city || '');
        setState(data.state || '');
        setPincode(data.pincode || '');
        setDob(data.date_of_birth ? data.date_of_birth.split('T')[0] : '');
        setAadhaarNumber(data.aadhaar_number || '');
        setPanNumber(data.pan_number || '');
        setEmergencyName(data.emergency_contact_name || '');
        setEmergencyPhone(data.emergency_contact_phone || '');
        setIdProofUrl(data.id_proof_url || null);
        setBankHolder(data.bank_account_holder || '');
        setBankAccount(data.bank_account_number || '');
        setBankIfsc(data.bank_ifsc || '');
        setUpiId(data.upi_id || '');
      }
    } catch (e: any) {
      console.warn('Failed to load profile:', e);
    }
  };

  // Modal Handlers
  const handleOpenAdd = () => {
    setEditingPro(null);
    setPendingProfileProId(null);
    setProfileSaveError(null);
    setFullName('');
    setPhone('');
    setCommissionPercent(10);
    setJoinedAt(new Date().toISOString().split('T')[0]);
    setSelectedSkillCategoryIds([]);
    setSelectedRoleLinkId('');
    setSelectedSupervisorId('');
    setPhotoUrl(null);
    setProIsActive(true);

    // Profile & Identity reset
    setAddressLine('');
    setCity('');
    setState('');
    setPincode('');
    setDob('');
    setAadhaarNumber('');
    setShowAadhaar(false);
    setPanNumber('');
    setEmergencyName('');
    setEmergencyPhone('');
    setIdProofUrl(null);
    setBankHolder('');
    setBankAccount('');
    setBankIfsc('');
    setUpiId('');
    setIsAddressExpanded(false);
    setIsBankExpanded(false);

    setShowModal(true);
  };

  const handleOpenEdit = (pro: ServiceProfessional) => {
    setEditingPro(pro);
    setPendingProfileProId(null);
    setProfileSaveError(null);
    setFullName(pro.full_name || '');
    setPhone(pro.phone || '');
    setCommissionPercent(pro.commission_percent ?? 0);
    setJoinedAt(pro.joined_at ? pro.joined_at.split('T')[0] : new Date().toISOString().split('T')[0]);
    
    // Extract existing skill category IDs
    const existingCatIds = pro.service_professional_skills?.map(s => s.category_id) || [];
    setSelectedSkillCategoryIds(existingCatIds);

    setSelectedRoleLinkId(pro.user_tenant_role_id || '');
    setSelectedSupervisorId(pro.supervisor_id || '');
    setPhotoUrl(pro.photo || null);
    setProIsActive(pro.is_active);

    // Profile & Identity reset & load
    setAddressLine('');
    setCity('');
    setState('');
    setPincode('');
    setDob('');
    setAadhaarNumber('');
    setShowAadhaar(false);
    setPanNumber('');
    setEmergencyName('');
    setEmergencyPhone('');
    setIdProofUrl(null);
    setBankHolder('');
    setBankAccount('');
    setBankIfsc('');
    setUpiId('');
    setIsAddressExpanded(false);
    setIsBankExpanded(false);

    fetchProProfile(pro.id);
    setShowModal(true);
  };

  const handleOpenProfile = (pro: ServiceProfessional) => {
    handleOpenEdit(pro);
    setIsAddressExpanded(true);
    setIsBankExpanded(true);
  };

  const handleRetryProfileSave = async () => {
    if (!pendingProfileProId || !supabase) return;
    setSaving(true);
    try {
      const { error: profileErr } = await supabase.rpc('save_professional_profile', {
        p_professional_id: pendingProfileProId,
        p_address_line: addressLine.trim() || null,
        p_city: city.trim() || null,
        p_state: state.trim() || null,
        p_pincode: pincode.trim() || null,
        p_aadhaar_number: aadhaarNumber.replace(/\s+/g, '').trim() || null,
        p_pan_number: panNumber.trim().toUpperCase() || null,
        p_date_of_birth: dob || null,
        p_emergency_contact_name: emergencyName.trim() || null,
        p_emergency_contact_phone: emergencyPhone.trim() || null,
        p_bank_account_holder: bankHolder.trim() || null,
        p_bank_account_number: bankAccount.trim() || null,
        p_bank_ifsc: bankIfsc.trim().toUpperCase() || null,
        p_upi_id: upiId.trim() || null,
        p_id_proof_url: idProofUrl || null,
      });

      if (profileErr) throw profileErr;

      toast.success('Professional profile saved successfully');
      setPendingProfileProId(null);
      setProfileSaveError(null);
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Retry save profile error:', err);
      setProfileSaveError(err.message);
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSkillCategory = (catId: string) => {
    setSelectedSkillCategoryIds(prev => 
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  // ID Proof Upload Handler
  const handleIdProofUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !supabase || !currentTenantId) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('ID proof size should be less than 5MB');
      return;
    }

    setUploadingIdProof(true);
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const cleanName = file.name.replace(/[^a-zA-Z0-9]/g, '_');
      const filePath = `${currentTenantId}/professionals/id_proofs/${Date.now()}_${cleanName}.${fileExt}`;

      const { error: uploadErr } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, { upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setIdProofUrl(urlData.publicUrl);
      toast.success('ID proof uploaded successfully');
    } catch (err: any) {
      console.error('ID proof upload error:', err);
      toast.error('ID proof upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingIdProof(false);
    }
  };

  // Photo Upload Handler
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !supabase || !currentTenantId) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo size should be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const cleanName = file.name.replace(/[^a-zA-Z0-9]/g, '_');
      const filePath = `${currentTenantId}/professionals/${Date.now()}_${cleanName}.${fileExt}`;

      const { error: uploadErr } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, { upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setPhotoUrl(urlData.publicUrl);
      toast.success('Photo uploaded successfully');
    } catch (err: any) {
      console.error('Photo upload error:', err);
      toast.error('Photo upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Save Professional Handler
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!fullName.trim()) {
      toast.error('Please enter professional full name');
      return;
    }

    if (!phone.trim()) {
      toast.error('Please enter contact phone number');
      return;
    }

    setSaving(true);
    try {
      // Ensure branch_id is available
      let branchToUse = resolvedBranchId;
      if (!branchToUse) {
        const { data: bData } = await supabase
          .from('branches')
          .select('id')
          .eq('tenant_id', currentTenantId)
          .limit(1)
          .maybeSingle();
        branchToUse = bData?.id || null;
      }

      if (!branchToUse) {
        throw new Error('No branch found for this business. Please create or configure a branch first.');
      }

      const payload: any = {
        tenant_id: currentTenantId,
        branch_id: branchToUse,
        user_tenant_role_id: selectedRoleLinkId || null,
        supervisor_id: selectedSupervisorId || null,
        full_name: fullName.trim(),
        phone: phone.trim(),
        commission_percent: Number(commissionPercent) || 0,
        photo: photoUrl || null,
        joined_at: joinedAt || null,
        is_active: proIsActive,
        updated_at: new Date().toISOString(),
      };

      let proId = editingPro?.id;

      if (editingPro) {
        // Update professional
        const { error: updateErr } = await supabase
          .from('service_professionals')
          .update(payload)
          .eq('id', editingPro.id);
        if (updateErr) throw updateErr;
      } else {
        // Insert professional
        const { data: newPro, error: insertErr } = await supabase
          .from('service_professionals')
          .insert(payload)
          .select('id')
          .single();
        if (insertErr) throw insertErr;
        proId = newPro.id;
      }

      // Sync Skills in service_professional_skills
      if (proId) {
        // 1. Delete existing skills for this professional
        const { error: delSkillsErr } = await supabase
          .from('service_professional_skills')
          .delete()
          .eq('professional_id', proId);
        if (delSkillsErr) throw delSkillsErr;

        // 2. Insert selected skills
        if (selectedSkillCategoryIds.length > 0) {
          const skillsToInsert = selectedSkillCategoryIds.map(catId => ({
            professional_id: proId,
            category_id: catId,
          }));
          const { error: addSkillsErr } = await supabase
            .from('service_professional_skills')
            .insert(skillsToInsert);
          if (addSkillsErr) throw addSkillsErr;
        }

        // 3. Save profile details via save_professional_profile RPC
        const { error: profileErr } = await supabase.rpc('save_professional_profile', {
          p_professional_id: proId,
          p_address_line: addressLine.trim() || null,
          p_city: city.trim() || null,
          p_state: state.trim() || null,
          p_pincode: pincode.trim() || null,
          p_aadhaar_number: aadhaarNumber.replace(/\s+/g, '').trim() || null,
          p_pan_number: panNumber.trim().toUpperCase() || null,
          p_date_of_birth: dob || null,
          p_emergency_contact_name: emergencyName.trim() || null,
          p_emergency_contact_phone: emergencyPhone.trim() || null,
          p_bank_account_holder: bankHolder.trim() || null,
          p_bank_account_number: bankAccount.trim() || null,
          p_bank_ifsc: bankIfsc.trim().toUpperCase() || null,
          p_upi_id: upiId.trim() || null,
          p_id_proof_url: idProofUrl || null,
        });

        if (profileErr) {
          console.error('Error in save_professional_profile:', profileErr);
          setPendingProfileProId(proId);
          setProfileSaveError(profileErr.message);
          toast.error(profileErr.message || 'Professional saved, but profile details failed to save.');
          await loadData();
          return;
        }
      }

      toast.success(
        editingPro
          ? `Updated details for ${fullName.trim()}`
          : `Added ${fullName.trim()} to Service Professionals`
      );
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Error saving professional:', err);
      // Exact Postgres error message surface
      toast.error(err.message || 'Failed to save professional');
    } finally {
      setSaving(false);
    }
  };

  // Toggle Active Status
  const handleToggleActive = async (pro: ServiceProfessional, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!supabase) return;
    const targetState = !pro.is_active;

    try {
      const { error } = await supabase
        .from('service_professionals')
        .update({
          is_active: targetState,
          updated_at: new Date().toISOString(),
        })
        .eq('id', pro.id);

      if (error) throw error;
      setProfessionals(prev =>
        prev.map(p => (p.id === pro.id ? { ...p, is_active: targetState } : p))
      );
      toast.success(`${pro.full_name} is now ${targetState ? 'Active' : 'Inactive'}`);
    } catch (err: any) {
      console.error('Error toggling active state:', err);
      toast.error(err.message || 'Failed to update status');
    }
  };

  // Delete Professional Handler
  const handleDeletePro = async () => {
    if (!deleteTarget || !supabase) return;
    setDeleting(true);

    try {
      // 1. First remove skills
      const { error: skillDelErr } = await supabase
        .from('service_professional_skills')
        .delete()
        .eq('professional_id', deleteTarget.id);
      if (skillDelErr) throw skillDelErr;

      // 2. Delete professional record
      const { error: proDelErr } = await supabase
        .from('service_professionals')
        .delete()
        .eq('id', deleteTarget.id);

      if (proDelErr) throw proDelErr;

      toast.success(`Removed ${deleteTarget.full_name} successfully.`);
      setDeleteTarget(null);
      await loadData();
    } catch (err: any) {
      console.error('Error deleting professional:', err);
      // Surface exact Postgres error message if blocked by foreign keys
      toast.error(err.message || 'Failed to delete professional');
    } finally {
      setDeleting(false);
    }
  };

  // Summary Metrics
  const totalStaff = professionals.length;
  const activeStaff = professionals.filter(p => p.is_active).length;
  const avgCommission = useMemo(() => {
    if (professionals.length === 0) return 0;
    const sum = professionals.reduce((acc, p) => acc + (Number(p.commission_percent) || 0), 0);
    return Math.round(sum / professionals.length);
  }, [professionals]);

  // Total supervisors count
  const totalSupervisors = useMemo(() => {
    return Object.keys(superviseeCountMap).length;
  }, [superviseeCountMap]);

  // Reusable Professional Card Component
  const renderProfessionalCard = (pro: ServiceProfessional) => {
    const skills = pro.service_professional_skills || [];
    const supervisorName = pro.supervisor_id ? supervisorNameMap.get(pro.supervisor_id) : null;
    const superviseeCount = superviseeCountMap[pro.id] || 0;

    return (
      <div
        key={pro.id}
        className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm hover:shadow-md transition-shadow relative flex flex-col justify-between"
      >
        <div>
          {/* Top: Avatar, Name, Phone & Status Toggle */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              {pro.photo ? (
                <img
                  src={pro.photo}
                  alt={pro.full_name}
                  className="w-12 h-12 rounded-full object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-base border border-primary/20 shrink-0">
                  {pro.full_name?.charAt(0)?.toUpperCase() || 'P'}
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                    {pro.full_name}
                  </h3>
                  {/* Supervises (N) badge */}
                  {superviseeCount > 0 && (
                    <span 
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shrink-0"
                      title={`${pro.full_name} supervises ${superviseeCount} team member${superviseeCount > 1 ? 's' : ''}`}
                    >
                      <UserCheck size={11} />
                      <span>Supervises ({superviseeCount})</span>
                    </span>
                  )}
                </div>
                <a
                  href={`tel:${pro.phone}`}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-primary flex items-center gap-1 mt-0.5"
                >
                  <Phone size={12} />
                  <span>{pro.phone}</span>
                </a>
              </div>
            </div>

            {/* Status Badge & Toggle */}
            <button
              onClick={e => handleToggleActive(pro, e)}
              title={`Click to ${pro.is_active ? 'deactivate' : 'activate'}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors shrink-0 ${
                pro.is_active
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
              }`}
            >
              {pro.is_active ? (
                <>
                  <CheckCircle2 size={12} />
                  <span>Active</span>
                </>
              ) : (
                <>
                  <XCircle size={12} />
                  <span>Inactive</span>
                </>
              )}
            </button>
          </div>

          {/* Reports To Badge / Row */}
          <div className="mb-3 flex items-center gap-1.5 text-xs">
            <span className="text-[11px] text-slate-400 font-medium">Reports To:</span>
            {supervisorName ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                <Users size={11} className="text-primary" />
                <span className="truncate max-w-[160px]">{supervisorName}</span>
              </span>
            ) : (
              <span className="text-slate-400 font-medium">—</span>
            )}
          </div>

          {/* Skills Badges */}
          <div className="mb-4">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Briefcase size={12} />
              <span>Skill Categories ({skills.length})</span>
            </p>
            {skills.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {skills.map(skill => (
                  <span
                    key={skill.id}
                    className="inline-block bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground text-[11px] font-medium px-2 py-0.5 rounded-md border border-primary/20"
                  >
                    {skill.service_categories?.category_name || 'Category'}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">No skills assigned yet</p>
            )}
          </div>

          {/* Commission & Joined Date Details */}
          <div className="grid grid-cols-2 gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 text-xs text-slate-600 dark:text-slate-300 mb-3 border border-slate-100 dark:border-slate-800">
            <div>
              <span className="text-[10px] text-slate-400 uppercase block font-semibold">Commission</span>
              <span className="font-bold text-slate-900 dark:text-white flex items-center gap-1 mt-0.5">
                <Percent size={13} className="text-primary" />
                {pro.commission_percent ?? 0}%
              </span>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase block font-semibold">Joined Date</span>
              <span className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-1 mt-0.5">
                <Calendar size={13} className="text-slate-400" />
                {pro.joined_at ? new Date(pro.joined_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>

          {/* City + KYC Badge Row */}
          {(() => {
            const kyc = Array.isArray(pro.service_professional_kyc)
              ? pro.service_professional_kyc[0]
              : pro.service_professional_kyc;
            const hasAadhaar = Boolean(kyc?.aadhaar_number && kyc.aadhaar_number.trim().length > 0);
            const hasAddress = Boolean(kyc?.address_line && kyc.address_line.trim().length > 0);
            const isKycAdded = hasAadhaar && hasAddress;
            const proCity = kyc?.city;
            const aadhaarRaw = (kyc?.aadhaar_number || '').replace(/\D/g, '');
            const maskedAadhaar = aadhaarRaw.length >= 4 
              ? `●●●● ●●●● ${aadhaarRaw.slice(-4)}`
              : '●●●● ●●●● ••••';

            return (
              <div className="flex items-center justify-between gap-2 p-2 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-xs border border-slate-100 dark:border-slate-800 mb-3">
                <div className="flex items-center gap-1.5 min-w-0">
                  <MapPin size={13} className="text-slate-400 shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300 font-medium truncate text-[11px]">
                    {proCity || 'City not set'}
                  </span>
                </div>

                {isKycAdded ? (
                  <span 
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 shrink-0"
                    title={`Aadhaar: ${maskedAadhaar}`}
                  >
                    <ShieldCheck size={11} />
                    <span>KYC added</span>
                    <span className="font-mono text-[9px] opacity-75">({maskedAadhaar})</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800 shrink-0">
                    <AlertCircle size={11} />
                    <span>KYC pending</span>
                  </span>
                )}
              </div>
            );
          })()}

          {/* Action Buttons: [Profile] and [Ledger] */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleOpenProfile(pro)}
              className="h-8 text-xs font-semibold gap-1.5 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <FileText size={13} className="text-primary" />
              <span>Profile</span>
            </Button>

            <Button
              type="button"
              size="sm"
              onClick={() => setSelectedLedgerPro(pro)}
              className="h-8 text-xs font-semibold gap-1.5 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-800 dark:hover:bg-slate-700"
            >
              <Wallet size={13} className="text-emerald-400" />
              <span>Ledger</span>
            </Button>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800 mt-auto gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            {pro.service_provider_id ? (
              <button
                type="button"
                onClick={() => setLoginManageTarget(pro)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 transition-colors"
                title="Manage app login"
              >
                <Smartphone size={11} />
                <span>App Login: Configured</span>
              </button>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setLoginManageTarget(pro)}
                className="h-6 text-[10px] font-semibold px-2 flex items-center gap-1 text-primary border-primary/30 hover:bg-primary/5 rounded-md"
                title="Configure app login for this professional"
              >
                <Smartphone size={11} />
                <span>Configure App Login</span>
              </Button>
            )}

            {pro.user_tenant_role_id ? (
              <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                <ShieldCheck size={11} />
                <span>System Role</span>
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenEdit(pro)}
              className="h-8 w-8 p-0 text-slate-600 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              title="Edit Professional"
            >
              <Pencil size={15} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(pro)}
              className="h-8 w-8 p-0 text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg"
              title="Delete Professional"
            >
              <Trash2 size={15} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Self as Professional Card (PATCH #2ad Task A) */}
      <div className="bg-gradient-to-r from-blue-50/90 via-indigo-50/50 to-slate-50 dark:from-slate-900 dark:via-blue-950/20 dark:to-slate-900 p-5 rounded-2xl border border-blue-200 dark:border-blue-900/60 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3.5">
            <div className="p-2.5 rounded-xl bg-blue-600/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5">
              <UserCheck size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-slate-900 dark:text-white">
                  Main khud service deta hoon
                </h2>
                {ownerProfessional ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    <CheckCircle2 size={12} />
                    Active as Professional
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                    Optional
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 max-w-2xl leading-relaxed">
                {ownerProfessional ? (
                  <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                    You are now listed as <strong>{ownerProfessional.full_name}</strong> (0% Commission). You are available in the Assign Professional dropdown and can work on jobs directly.
                  </span>
                ) : (
                  <span>
                    Separate staff professionals are not compulsory. Enable yourself to take customer bookings, execute service jobs, and manage your business without hiring extra staff.
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
            {ownerProfessional ? (
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleEnableSelfAsProfessional}
                  disabled={enablingSelf}
                  variant="outline"
                  size="sm"
                  className="border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 text-xs font-medium rounded-xl"
                  title="Already enabled. Click to verify or re-sync"
                >
                  {enablingSelf ? (
                    <>
                      <Loader2 size={13} className="animate-spin mr-1.5" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={13} className="mr-1.5 text-emerald-600" />
                      Enabled as Professional
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleEnableSelfAsProfessional}
                disabled={enablingSelf}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-4 py-2 rounded-xl shadow-sm flex items-center gap-1.5"
              >
                {enablingSelf ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <UserPlus size={14} />
                    Enable myself as Professional
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Real Error Text on Failure */}
        {selfEnableError && (
          <div className="mt-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-xs text-rose-700 dark:text-rose-300 flex items-start gap-2">
            <AlertCircle size={15} className="shrink-0 mt-0.5 text-rose-600" />
            <div>
              <p className="font-semibold">Failed to enable self as professional:</p>
              <p className="mt-0.5">{selfEnableError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Top Banner / Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-primary/10 text-primary">
              <Users size={22} />
            </span>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Staff & Team</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage service professionals, stylists, technicians, supervisor hierarchy, and commission rates.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={handleOpenAdd}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium px-4 py-2 rounded-xl shadow-sm transition-all"
          >
            <Plus size={16} />
            <span>Add Professional</span>
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Staff</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{totalStaff}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Active Staff</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{activeStaff}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Supervisors</p>
            <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">{totalSupervisors}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Avg Commission</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{avgCommission}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter, Search & View-As Toggle Bar */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col lg:flex-row gap-3 items-center justify-between">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
          {/* Search Box */}
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search staff, phone or skill..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm"
            />
          </div>

          {/* View as: All Staff | By Supervisor */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl text-xs font-semibold shrink-0 w-full sm:w-auto justify-center">
            <span className="text-slate-400 px-2 text-[11px] uppercase tracking-wider hidden sm:inline">View as:</span>
            <button
              type="button"
              onClick={() => setViewGrouping('all')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                viewGrouping === 'all'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs font-bold'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Layers size={13} />
              <span>All Staff</span>
            </button>
            <button
              type="button"
              onClick={() => setViewGrouping('by_supervisor')}
              className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                viewGrouping === 'by_supervisor'
                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs font-bold'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <Users size={13} />
              <span>By Supervisor</span>
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto justify-end">
          {/* Category Filter */}
          <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
            <Filter size={14} className="text-slate-400" />
            <select
              value={selectedCategoryFilter}
              onChange={e => setSelectedCategoryFilter(e.target.value)}
              className="h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All Skill Categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as any)}
            className="h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Main Staff Content */}
      {loading ? (
        <div className="p-12 flex flex-col items-center justify-center text-slate-400">
          <Loader2 size={32} className="animate-spin mb-3 text-primary" />
          <p className="text-sm">Loading staff members...</p>
        </div>
      ) : filteredProfessionals.length === 0 ? (
        <Card className="border border-dashed border-slate-300 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 p-12 text-center rounded-2xl">
          <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-4">
              <Users size={24} />
            </div>
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">
              No service professionals found
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
              {searchQuery || selectedCategoryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by adding your technicians, stylists, or service professionals.'}
            </p>
            <Button
              onClick={handleOpenAdd}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-xl"
            >
              <Plus size={16} />
              <span>Add First Professional</span>
            </Button>
          </div>
        </Card>
      ) : viewGrouping === 'by_supervisor' ? (
        /* ========================================================= */
        /* Grouped by Supervisor View (Collapsible Sections)         */
        /* ========================================================= */
        <div className="space-y-6">
          {groupedBySupervisor.map(group => {
            const groupKey = group.supervisorId || 'unassigned';
            const isCollapsed = !!collapsedGroups[groupKey];

            return (
              <div
                key={groupKey}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm"
              >
                {/* Collapsible Section Header */}
                <button
                  type="button"
                  onClick={() => toggleGroupCollapse(groupKey)}
                  className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="p-1 rounded-md bg-white dark:bg-slate-700 text-slate-500 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                      {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                    </span>

                    {group.supervisorId ? (
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 font-bold text-xs flex items-center justify-center border border-indigo-200 dark:border-indigo-800">
                          {group.supervisorName.charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs uppercase font-bold text-slate-400 tracking-wider">Supervisor:</span>
                            <span className="text-sm font-bold text-slate-900 dark:text-white">
                              {group.supervisorName}
                            </span>
                          </div>
                          {group.supervisorPro?.phone && (
                            <span className="text-[11px] text-slate-500 dark:text-slate-400">
                              Phone: {group.supervisorPro.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2.5">
                        <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 font-bold text-xs flex items-center justify-center border border-slate-200 dark:border-slate-700">
                          <UserX size={16} />
                        </span>
                        <div>
                          <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                            Unassigned / Direct Staff
                          </span>
                          <p className="text-[11px] text-slate-400">
                            Professionals without an assigned supervisor
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                    <Users size={12} className="text-primary" />
                    <span>
                      {group.members.length} {group.members.length === 1 ? 'Member' : 'Members'}
                    </span>
                  </span>
                </button>

                {/* Subordinates Grid */}
                {!isCollapsed && (
                  <div className="p-5 border-t border-slate-100 dark:border-slate-800">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {group.members.map(member => renderProfessionalCard(member))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ========================================================= */
        /* All Staff Flat Grid View                                  */
        /* ========================================================= */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProfessionals.map(pro => renderProfessionalCard(pro))}
        </div>
      )}

      {/* Add / Edit Professional Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {editingPro ? 'Edit Service Professional' : 'Add New Service Professional'}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Configure staff profile, assigned skill categories, supervisor, and commission.
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body / Form */}
            <form onSubmit={handleSave} className="overflow-y-auto p-6 space-y-5">
              {/* Photo & Basic Details */}
              <div className="flex flex-col sm:flex-row items-center gap-5 pb-2">
                <div className="relative group">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-primary/20 shadow-sm"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-700 flex flex-col items-center justify-center text-slate-400">
                      <Users size={24} />
                      <span className="text-[9px] mt-1">Photo</span>
                    </div>
                  )}

                  <label className="absolute inset-0 rounded-full bg-black/40 text-white flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                    {uploadingPhoto ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        <Upload size={16} />
                        <span className="text-[9px] mt-0.5">Upload</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      className="hidden"
                    />
                  </label>
                </div>

                <div className="flex-1 w-full space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Full Name <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      placeholder="e.g. Rahul Sharma"
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      required
                      className="h-10 text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Phone Number <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      placeholder="e.g. 9876543210"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      required
                      className="h-10 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Commission & Joined Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Commission Rate (%)
                  </label>
                  <div className="relative">
                    <Percent size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      placeholder="e.g. 15"
                      value={commissionPercent}
                      onChange={e => setCommissionPercent(e.target.value)}
                      className="pl-9 h-10 text-sm"
                    />
                  </div>
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    Percentage payout on services completed by this professional.
                  </span>
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Joined Date
                  </label>
                  <div className="relative">
                    <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="date"
                      value={joinedAt}
                      onChange={e => setJoinedAt(e.target.value)}
                      className="pl-9 h-10 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Reports To / Supervisor Dropdown */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Reports To / Supervisor
                </label>
                <select
                  value={selectedSupervisorId}
                  onChange={e => setSelectedSupervisorId(e.target.value)}
                  className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">None (No Supervisor / Direct)</option>
                  {availableSupervisors.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.full_name} ({s.phone})
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Assign a senior professional or manager who oversees this staff member. Self-selection is prevented.
                </span>
              </div>

              {/* Skill Categories Multi-Select */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">
                  Assigned Skill Categories
                </label>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-2">
                  Select all services / categories this professional is certified to perform:
                </p>

                {categories.length === 0 ? (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 text-xs text-slate-500 text-center">
                    No categories found. Please add categories in Service Catalog first.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto p-2 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700">
                    {categories.map(cat => {
                      const isSelected = selectedSkillCategoryIds.includes(cat.id);
                      return (
                        <button
                          type="button"
                          key={cat.id}
                          onClick={() => handleToggleSkillCategory(cat.id)}
                          className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium text-left transition-all border ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-xs'
                              : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-primary/40'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded flex items-center justify-center text-[10px] shrink-0 border ${
                              isSelected
                                ? 'bg-white text-primary border-white'
                                : 'border-slate-300 dark:border-slate-600'
                            }`}
                          >
                            {isSelected && '✓'}
                          </div>
                          <span className="truncate">{cat.category_name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Link to System Role (Optional) */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Link System User Account (Optional)
                </label>
                <select
                  value={selectedRoleLinkId}
                  onChange={e => setSelectedRoleLinkId(e.target.value)}
                  className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">None (Independent / Non-portal staff)</option>
                  {tenantRoles.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.role_name?.toUpperCase()} (ID: {r.id.slice(0, 8)}...)
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Allows mapping this professional to a login credential for mobile app or dashboard access.
                </span>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div>
                  <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 block">
                    Active Status
                  </span>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    Inactive staff will not be available for customer bookings or assignment.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setProIsActive(!proIsActive)}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                    proIsActive ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-700'
                  }`}
                >
                  <div
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                      proIsActive ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Collapsible Section 1: Address & Identity */}
              <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-slate-50/50 dark:bg-slate-800/30">
                <button
                  type="button"
                  onClick={() => setIsAddressExpanded(!isAddressExpanded)}
                  className="w-full flex items-center justify-between p-3.5 text-left hover:bg-slate-100/60 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    <span className="text-xs font-bold text-slate-900 dark:text-white">
                      Address & Identity
                    </span>
                    {(addressLine || aadhaarNumber) && (
                      <span className="text-[10px] bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full font-medium">
                        Info entered
                      </span>
                    )}
                  </div>
                  {isAddressExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                </button>

                {isAddressExpanded && (
                  <div className="p-4 pt-1 space-y-3.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                        Full Address
                      </label>
                      <Textarea
                        placeholder="House / Street / Area / Landmark"
                        value={addressLine}
                        onChange={e => setAddressLine(e.target.value)}
                        rows={2}
                        className="text-xs"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          City
                        </label>
                        <Input
                          placeholder="e.g. Bahraich"
                          value={city}
                          onChange={e => setCity(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          State
                        </label>
                        <Input
                          placeholder="e.g. Uttar Pradesh"
                          value={state}
                          onChange={e => setState(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          Pincode (6 digits)
                        </label>
                        <Input
                          placeholder="e.g. 271801"
                          maxLength={6}
                          value={pincode}
                          onChange={e => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="h-9 text-xs font-mono"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          Date of Birth
                        </label>
                        <Input
                          type="date"
                          value={dob}
                          onChange={e => setDob(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                            Aadhaar Number
                          </label>
                          {aadhaarNumber && (
                            <button
                              type="button"
                              onClick={() => setShowAadhaar(!showAadhaar)}
                              className="text-[11px] text-primary hover:underline flex items-center gap-1"
                            >
                              {showAadhaar ? <EyeOff size={12} /> : <Eye size={12} />}
                              <span>{showAadhaar ? 'Mask' : 'Show'}</span>
                            </button>
                          )}
                        </div>
                        <Input
                          type="text"
                          placeholder="1234 5678 9012"
                          maxLength={14}
                          value={
                            !showAadhaar && aadhaarNumber.replace(/\s+/g, '').length === 12
                              ? `●●●● ●●●● ${aadhaarNumber.replace(/\s+/g, '').slice(-4)}`
                              : aadhaarNumber
                          }
                          onChange={e => {
                            setShowAadhaar(true);
                            setAadhaarNumber(formatAadhaarInput(e.target.value));
                          }}
                          className="h-9 text-xs font-mono"
                        />
                        <span className="text-[10px] text-slate-400 mt-0.5 block">
                          12 digits formatted 1234 5678 9012
                        </span>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          PAN Number
                        </label>
                        <Input
                          placeholder="ABCDE1234F"
                          maxLength={10}
                          value={panNumber}
                          onChange={e => setPanNumber(e.target.value.toUpperCase())}
                          className="h-9 text-xs font-mono uppercase"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-slate-100 dark:border-slate-800">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          Emergency Contact Name
                        </label>
                        <Input
                          placeholder="e.g. Spouse / Parent"
                          value={emergencyName}
                          onChange={e => setEmergencyName(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          Emergency Contact Phone
                        </label>
                        <Input
                          placeholder="e.g. 9876543210"
                          value={emergencyPhone}
                          onChange={e => setEmergencyPhone(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Collapsible Section 2: Bank details */}
              <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-slate-50/50 dark:bg-slate-800/30">
                <button
                  type="button"
                  onClick={() => setIsBankExpanded(!isBankExpanded)}
                  className="w-full flex items-center justify-between p-3.5 text-left hover:bg-slate-100/60 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-primary" />
                    <span className="text-xs font-bold text-slate-900 dark:text-white">
                      Bank details
                    </span>
                    {(bankAccount || upiId) && (
                      <span className="text-[10px] bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full font-medium">
                        Info entered
                      </span>
                    )}
                  </div>
                  {isBankExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                </button>

                {isBankExpanded && (
                  <div className="p-4 pt-1 space-y-3.5 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          Account Holder Name
                        </label>
                        <Input
                          placeholder="Name as per bank account"
                          value={bankHolder}
                          onChange={e => setBankHolder(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          Account Number
                        </label>
                        <Input
                          placeholder="e.g. 100293847561"
                          value={bankAccount}
                          onChange={e => setBankAccount(e.target.value)}
                          className="h-9 text-xs font-mono"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          IFSC Code
                        </label>
                        <Input
                          placeholder="e.g. SBIN0001234"
                          maxLength={11}
                          value={bankIfsc}
                          onChange={e => setBankIfsc(e.target.value.toUpperCase())}
                          className="h-9 text-xs font-mono uppercase"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                          UPI ID / VPA
                        </label>
                        <Input
                          placeholder="e.g. name@okhdfcbank"
                          value={upiId}
                          onChange={e => setUpiId(e.target.value)}
                          className="h-9 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile Save Error Banner with Retry */}
              {profileSaveError && (
                <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs text-rose-800 dark:text-rose-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} className="text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Profile Save Failed</p>
                      <p className="text-[11px] text-rose-700 dark:text-rose-300">{profileSaveError}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleRetryProfileSave}
                    disabled={saving}
                    className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white shrink-0 self-end sm:self-center"
                  >
                    {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
                    <span>Retry profile save</span>
                  </Button>
                </div>
              )}

              {/* Service Professional App Login Management */}
              {editingPro && currentTenantId && (
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <ProfessionalLoginCard
                    tenantId={currentTenantId}
                    professional={editingPro}
                    onLoginCreated={loadData}
                  />
                </div>
              )}

              {/* Modal Footer Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-5"
                >
                  {saving ? (
                    <div className="flex items-center gap-1.5">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Saving...</span>
                    </div>
                  ) : editingPro ? (
                    'Save Changes'
                  ) : (
                    'Add Professional'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-rose-500 mb-3">
              <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/50">
                <AlertCircle size={22} />
              </div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Delete Professional</h3>
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
              Are you sure you want to remove <strong>{deleteTarget.full_name}</strong>?
              This will also unassign all mapped skill categories.
            </p>

            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeletePro}
                disabled={deleting}
                className="text-xs bg-rose-600 hover:bg-rose-700"
              >
                {deleting ? (
                  <div className="flex items-center gap-1.5">
                    <Loader2 size={13} className="animate-spin" />
                    <span>Removing...</span>
                  </div>
                ) : (
                  'Yes, Remove'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dedicated Partner App Login Modal */}
      {loginManageTarget && currentTenantId && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-5 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-700/60">
              <div className="flex items-center gap-2">
                <Smartphone size={18} className="text-primary" />
                <h3 className="text-sm font-bold text-white">
                  Partner App Login: {loginManageTarget.full_name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setLoginManageTarget(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={16} />
              </button>
            </div>
            <ProfessionalLoginCard
              tenantId={currentTenantId}
              professional={loginManageTarget}
              onLoginCreated={() => {
                loadData();
              }}
            />
          </div>
        </div>
      )}

      {/* Professional Ledger Drawer */}
      {selectedLedgerPro && (
        <ProfessionalLedgerDrawer
          isOpen={!!selectedLedgerPro}
          onClose={() => setSelectedLedgerPro(null)}
          professionalId={selectedLedgerPro.id}
          professionalName={selectedLedgerPro.full_name}
        />
      )}
    </div>
  );
}
